import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// A cinematic launch intro for the platform reveal. Plays ~45s of sequenced,
// animated scenes (brand, moving charts, messaging, apps orbit) then lands on the
// login page. Replayable — every visit to /launch runs it fresh. Self-contained
// (inline CSS keyframes, SVG animations; no dependencies).
export default function LaunchPage() {
  const navigate = useNavigate();
  const [scene, setScene] = useState(0);
  const [skip, setSkip] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    // scene schedule (ms): total ~45s, then -> login
    const seq = [
      [0, 0],       // brand ignite
      [6500, 1],    // "project tracking, made effortless"
      [13000, 2],   // live charts building
      [21000, 3],   // pillars: automation / in-house data / security
      [29000, 4],   // the apps orbit
      [37000, 5],   // final brand lockup
    ];
    seq.forEach(([t, s]) => timers.current.push(setTimeout(() => setScene(s), t)));
    timers.current.push(setTimeout(() => navigate('/login'), 45000));
    return () => timers.current.forEach(clearTimeout);
  }, [navigate]);

  const goNow = () => { timers.current.forEach(clearTimeout); navigate('/login'); };

  return (
    <div style={S.stage}>
      <style>{CSS}</style>

      {/* animated background field */}
      <div style={S.bg}>
        <div className="grid" />
        <div className="glow glow1" />
        <div className="glow glow2" />
        {[...Array(28)].map((_, i) => <span key={i} className="spark" style={{ left: `${(i * 37) % 100}%`, animationDelay: `${(i % 9) * 0.6}s`, animationDuration: `${6 + (i % 5)}s` }} />)}
      </div>

      {/* SCENE 0 — brand ignite */}
      {scene === 0 && (
        <div className="scene">
          <div className="ring" />
          <div className="brandMark">EOMP</div>
          <div className="brandSub fade-up d1">Enterprise Operations Management Platform</div>
          <div className="brandBy fade-up d2">Space Communication Technologies · Oman</div>
        </div>
      )}

      {/* SCENE 1 — headline */}
      {scene === 1 && (
        <div className="scene">
          <div className="kicker fade-up">Project management, reimagined</div>
          <div className="headline">
            <span className="word w1">Plan.</span>{' '}
            <span className="word w2">Track.</span>{' '}
            <span className="word w3">Deliver.</span>
          </div>
          <div className="sub fade-up d3">Every project, every milestone — visible in real time.</div>
        </div>
      )}

      {/* SCENE 2 — charts building */}
      {scene === 2 && (
        <div className="scene wide">
          <div className="kicker fade-up">Progress you can see</div>
          <div className="charts">
            <div className="bars">
              {[52, 74, 63, 88, 70, 95].map((h, i) => <div key={i} className="bar" style={{ '--h': `${h}%`, animationDelay: `${0.15 * i}s` }} />)}
            </div>
            <svg className="line" viewBox="0 0 320 120" preserveAspectRatio="none">
              <polyline className="lp" points="0,100 55,78 110,84 165,50 220,58 275,24 320,30" />
              <circle className="dot" r="4" />
            </svg>
            <div className="donut">
              <svg viewBox="0 0 120 120"><circle className="dtrack" cx="60" cy="60" r="48" /><circle className="dval" cx="60" cy="60" r="48" /></svg>
              <div className="dpct">87%</div>
            </div>
          </div>
          <div className="sub fade-up d4">Milestones, budgets and teams — always in sync.</div>
        </div>
      )}

      {/* SCENE 3 — pillars */}
      {scene === 3 && (
        <div className="scene wide">
          <div className="kicker fade-up">One platform. Built for the enterprise.</div>
          <div className="pillars">
            {[
              ['⚙', 'Automation', 'Workflows that run themselves'],
              ['🇴🇲', 'In-house, in Oman', 'Your data stays on your servers'],
              ['🔒', 'Secure by design', 'Enterprise-grade protection'],
            ].map((p, i) => (
              <div key={i} className="pillar" style={{ animationDelay: `${0.25 * i}s` }}>
                <div className="picon">{p[0]}</div>
                <div className="ptitle">{p[1]}</div>
                <div className="pdesc">{p[2]}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SCENE 4 — apps orbit */}
      {scene === 4 && (
        <div className="scene">
          <div className="kicker fade-up">Everything your operation needs</div>
          <div className="orbit">
            <div className="core">EOMP</div>
            {['Projects', 'Finance', 'CRM', 'Attendance', 'EDRMS', 'HR AI', 'Tech Ops', 'IPP'].map((a, i) => (
              <div key={a} className="node" style={{ '--i': i, '--n': 8 }}><span>{a}</span></div>
            ))}
          </div>
          <div className="sub fade-up d3">Projects at the centre — connected to everything else.</div>
        </div>
      )}

      {/* SCENE 5 — final lockup */}
      {scene === 5 && (
        <div className="scene">
          <div className="ring slow" />
          <div className="brandMark big">EOMP</div>
          <div className="tagline fade-up d1">Run the enterprise. Deliver the projects.</div>
          <div className="brandBy fade-up d2">Space Communication Technologies</div>
          <button className="enterBtn fade-up d3" onClick={goNow}>Enter the platform →</button>
        </div>
      )}

      {/* progress + skip */}
      <div style={S.progWrap}><div className="prog" /></div>
      {!skip && <button style={S.skip} onClick={() => { setSkip(true); goNow(); }}>Skip intro →</button>}
    </div>
  );
}

const S = {
  stage: { position: 'fixed', inset: 0, background: '#05070d', overflow: 'hidden', fontFamily: "'Inter', system-ui, sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' },
  bg: { position: 'absolute', inset: 0, overflow: 'hidden' },
  progWrap: { position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: 'rgba(255,255,255,0.08)' },
  skip: { position: 'absolute', top: 22, right: 26, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20, padding: '7px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', backdropFilter: 'blur(6px)', zIndex: 10 },
};

const CSS = `
.grid{position:absolute;inset:-50%;background-image:linear-gradient(rgba(56,189,248,.06) 1px,transparent 1px),linear-gradient(90deg,rgba(56,189,248,.06) 1px,transparent 1px);background-size:44px 44px;transform:perspective(600px) rotateX(60deg);animation:gridmove 18s linear infinite;}
@keyframes gridmove{from{background-position:0 0}to{background-position:0 44px}}
.glow{position:absolute;border-radius:50%;filter:blur(90px);opacity:.5;}
.glow1{width:520px;height:520px;background:#0ea5a5;top:-140px;left:-120px;animation:float1 14s ease-in-out infinite;}
.glow2{width:480px;height:480px;background:#7c3aed;bottom:-160px;right:-120px;animation:float2 16s ease-in-out infinite;}
@keyframes float1{50%{transform:translate(120px,80px)}}
@keyframes float2{50%{transform:translate(-100px,-70px)}}
.spark{position:absolute;top:-10px;width:2px;height:2px;background:#7dd3fc;border-radius:50%;opacity:.6;animation:fall linear infinite;}
@keyframes fall{to{transform:translateY(110vh);opacity:0}}

.scene{position:relative;z-index:5;text-align:center;padding:24px;max-width:1000px;animation:sceneIn .9s cubic-bezier(.2,.7,.2,1);}
.scene.wide{max-width:1100px;width:92%;}
@keyframes sceneIn{from{opacity:0;transform:translateY(18px) scale(.98)}to{opacity:1;transform:none}}

.ring{width:220px;height:220px;border-radius:50%;border:2px solid rgba(56,189,248,.35);border-top-color:#22d3ee;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);animation:spin 3.2s linear infinite;box-shadow:0 0 60px rgba(34,211,238,.25) inset;}
.ring.slow{animation-duration:6s;width:280px;height:280px;}
@keyframes spin{to{transform:translate(-50%,-50%) rotate(360deg)}}

.brandMark{font-size:90px;font-weight:900;letter-spacing:-3px;background:linear-gradient(120deg,#22d3ee,#818cf8,#c084fc);-webkit-background-clip:text;background-clip:text;color:transparent;animation:ignite 1.2s cubic-bezier(.2,.8,.2,1);text-shadow:0 0 60px rgba(129,140,248,.3);}
.brandMark.big{font-size:110px;}
@keyframes ignite{from{opacity:0;transform:scale(.6);filter:blur(14px)}to{opacity:1;transform:none;filter:none}}
.brandSub{font-size:17px;color:rgba(255,255,255,.75);margin-top:10px;letter-spacing:.5px;}
.brandBy{font-size:13px;color:rgba(255,255,255,.4);margin-top:6px;}
.tagline{font-size:22px;font-weight:600;color:#e2e8f0;margin-top:14px;}
.enterBtn{margin-top:26px;background:linear-gradient(120deg,#0ea5a5,#7c3aed);color:#fff;border:none;border-radius:30px;padding:13px 30px;font-size:15px;font-weight:700;cursor:pointer;box-shadow:0 10px 40px rgba(124,58,237,.4);}
.enterBtn:hover{transform:translateY(-2px);}

.kicker{font-size:14px;letter-spacing:3px;text-transform:uppercase;color:#5eead4;font-weight:700;margin-bottom:18px;}
.headline{font-size:64px;font-weight:900;letter-spacing:-2px;line-height:1.05;}
.word{display:inline-block;opacity:0;background:linear-gradient(120deg,#fff,#a5b4fc);-webkit-background-clip:text;background-clip:text;color:transparent;animation:pop .7s forwards;}
.w1{animation-delay:.1s}.w2{animation-delay:.5s}.w3{animation-delay:.9s}
@keyframes pop{from{opacity:0;transform:translateY(26px) scale(.9)}to{opacity:1;transform:none}}
.sub{font-size:18px;color:rgba(255,255,255,.7);margin-top:20px;}

.fade-up{opacity:0;animation:fadeUp .8s forwards;}
.d1{animation-delay:.5s}.d2{animation-delay:.9s}.d3{animation-delay:1.3s}.d4{animation-delay:1.6s}
@keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}

.charts{display:flex;gap:40px;align-items:flex-end;justify-content:center;margin:30px 0;flex-wrap:wrap;}
.bars{display:flex;gap:12px;align-items:flex-end;height:150px;}
.bar{width:26px;height:0;align-self:flex-end;background:linear-gradient(180deg,#22d3ee,#0ea5a5);border-radius:6px 6px 0 0;animation:grow 1s forwards cubic-bezier(.2,.8,.2,1);box-shadow:0 0 20px rgba(34,211,238,.3);}
@keyframes grow{to{height:var(--h)}}
.line{width:320px;height:120px;}
.lp{fill:none;stroke:#a78bfa;stroke-width:3;stroke-linecap:round;stroke-dasharray:600;stroke-dashoffset:600;animation:draw 2s forwards ease;filter:drop-shadow(0 0 8px rgba(167,139,250,.5));}
@keyframes draw{to{stroke-dashoffset:0}}
.dot{fill:#fff;offset-path:path('M0,100 55,78 110,84 165,50 220,58 275,24 320,30');animation:none;}
.donut{position:relative;width:120px;height:120px;}
.dtrack{fill:none;stroke:rgba(255,255,255,.1);stroke-width:12;}
.dval{fill:none;stroke:#34d399;stroke-width:12;stroke-linecap:round;stroke-dasharray:301;stroke-dashoffset:301;transform:rotate(-90deg);transform-origin:60px 60px;animation:fill 1.8s forwards ease;filter:drop-shadow(0 0 8px rgba(52,211,153,.5));}
@keyframes fill{to{stroke-dashoffset:40}}
.dpct{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:24px;font-weight:800;}

.pillars{display:flex;gap:26px;justify-content:center;margin-top:24px;flex-wrap:wrap;}
.pillar{width:240px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);border-radius:18px;padding:26px 20px;opacity:0;animation:cardIn .8s forwards cubic-bezier(.2,.8,.2,1);backdrop-filter:blur(8px);}
@keyframes cardIn{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}
.picon{font-size:38px;margin-bottom:12px;}
.ptitle{font-size:19px;font-weight:800;margin-bottom:6px;}
.pdesc{font-size:13.5px;color:rgba(255,255,255,.6);}

.orbit{position:relative;width:440px;height:440px;margin:20px auto;}
.core{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:120px;height:120px;border-radius:50%;background:linear-gradient(135deg,#0ea5a5,#7c3aed);display:flex;align-items:center;justify-content:center;font-weight:900;font-size:24px;box-shadow:0 0 60px rgba(124,58,237,.5);z-index:3;animation:corepulse 2.5s ease-in-out infinite;}
@keyframes corepulse{50%{box-shadow:0 0 90px rgba(34,211,238,.6)}}
.node{position:absolute;top:50%;left:50%;width:84px;height:84px;margin:-42px;border-radius:50%;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.15);display:flex;align-items:center;justify-content:center;backdrop-filter:blur(6px);opacity:0;animation:nodeIn .6s forwards, orbitSpin 24s linear infinite;transform-origin:center;transform:rotate(calc(360deg / var(--n) * var(--i))) translate(180px) rotate(calc(-360deg / var(--n) * var(--i)));animation-delay:calc(.12s * var(--i)), 0s;}
.node span{font-size:12.5px;font-weight:700;color:#e2e8f0;}
@keyframes nodeIn{from{opacity:0;transform:rotate(calc(360deg / var(--n) * var(--i))) translate(60px) rotate(calc(-360deg / var(--n) * var(--i)))}to{opacity:1}}

.prog{height:100%;background:linear-gradient(90deg,#22d3ee,#7c3aed);width:0;animation:progress 45s linear forwards;}
@keyframes progress{to{width:100%}}
`;
